import { Component, type ReactNode, type ErrorInfo } from 'react';
import { logger } from '../stores/playerStore';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

/**
 * Error boundary component that catches JavaScript errors anywhere in the child
 * component tree and displays a fallback UI.
 */
export class ErrorBoundary extends Component<Props, State> {
  private timestamp: string = '';

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.timestamp = new Date().toISOString();
    this.setState({ errorInfo });
    logger.error(
      {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      },
      'React error boundary caught an error'
    );
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
  };

  handleCopyDetails = (): void => {
    const { error, errorInfo } = this.state;
    const details = [
      `Error: ${error?.message || 'Unknown error'}`,
      `Timestamp: ${this.timestamp}`,
      `User Agent: ${navigator.userAgent}`,
      '',
      'Stack Trace:',
      error?.stack || 'No stack trace available',
      '',
      'Component Stack:',
      errorInfo?.componentStack || 'No component stack available',
    ].join('\n');

    navigator.clipboard.writeText(details).catch(() => {
      // Clipboard API might not be available
    });
  };

  toggleDetails = (): void => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex flex-col items-center justify-center safe-area bg-gradient-to-br from-gray-900 to-gray-800 p-6">
          {/* Error icon */}
          <div className="w-20 h-20 mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
            <svg
              className="w-10 h-10 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h1 className="text-xl font-bold text-white mb-2">Something went wrong</h1>
          <p className="text-gray-400 text-center mb-2 max-w-xs">
            {this.state.error?.message || 'An unexpected error occurred. Please try again.'}
          </p>

          {this.state.error && (
            <div className="mb-6 w-full max-w-md">
              <button
                onClick={this.toggleDetails}
                className="flex items-center gap-2 text-gray-500 text-sm hover:text-gray-400 transition-colors"
              >
                <svg
                  className={`w-4 h-4 transition-transform ${this.state.showDetails ? 'rotate-90' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                {this.state.showDetails ? 'Hide Details' : 'Show Details'}
              </button>

              {this.state.showDetails && (
                <div className="mt-3 p-4 bg-gray-800 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-xs text-gray-500">Error Details</span>
                    <button
                      onClick={this.handleCopyDetails}
                      className="text-xs text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      Copy
                    </button>
                  </div>

                  <div className="space-y-3 text-xs">
                    <div>
                      <span className="text-gray-500 block mb-1">Timestamp</span>
                      <span className="text-gray-300 font-mono">{this.timestamp}</span>
                    </div>

                    <div>
                      <span className="text-gray-500 block mb-1">Stack Trace</span>
                      <pre className="text-red-400 overflow-auto max-h-32 whitespace-pre-wrap font-mono">
                        {this.state.error.stack}
                      </pre>
                    </div>

                    {this.state.errorInfo?.componentStack && (
                      <div>
                        <span className="text-gray-500 block mb-1">Component Stack</span>
                        <pre className="text-orange-400 overflow-auto max-h-24 whitespace-pre-wrap font-mono">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}

                    <div>
                      <span className="text-gray-500 block mb-1">Browser</span>
                      <span className="text-gray-300 font-mono break-all">
                        {navigator.userAgent}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="space-y-3 w-full max-w-xs">
            <button
              onClick={this.handleRetry}
              className="w-full px-8 py-4 bg-primary-600 hover:bg-primary-500 active:scale-95 rounded-xl text-lg font-semibold transition-all"
            >
              Try Again
            </button>
            <button
              onClick={this.handleReload}
              className="w-full px-8 py-3 bg-gray-700 hover:bg-gray-600 active:scale-95 rounded-xl text-gray-300 transition-all"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
